var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create } from "zustand";
import { dispatchTrackingEvent } from "~assets/global/tracking/tracking-event";
import { preventScrolling } from "~assets/frontend/_util/preventScrolling";
import { IBEXA_SEARCH_ENDPOINT } from "~assets/frontend/_util/api-endpoints";
let timeoutId = null;
let fetchSearchResultsController = null;
let fetchMoreResultsController = null;
const useSearchStore = create()((set, get) => ({
    query: "",
    showHeader: false,
    results: null,
    loadingMain: null,
    loadingMore: null,
    actions: {
        // smells like https://react.dev/learn/you-might-not-need-an-effect#chains-of-computations :-D
        // probably not that bad though, but easy without effect:
        toggleHeader: (doShow) => {
            set((state) => ({
                showHeader: doShow,
            }));
            preventScrolling(doShow);
            dispatchTrackingEvent({
                name: "ui",
                payload: { ui_component: "frm-search", ui_function: "toggle", ui_action: doShow ? "show" : "hide", ui_target: null },
            });
        },
        closeSearch: () => {
            set((state) => ({
                query: "",
                showHeader: false,
                showBody: false,
                results: null,
            }));
            preventScrolling(false);
        },
        handleSearchInput: (value) => {
            set((state) => ({
                query: value,
                showHeader: true,
                showBody: value.length > 0,
                results: null,
            }));
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (fetchSearchResultsController) {
                fetchSearchResultsController.abort();
            }
            if (fetchMoreResultsController) {
                fetchMoreResultsController.abort();
            }
            if (value.length > 0) {
                timeoutId = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    const uiEvent = {
                        name: "ui",
                        payload: {
                            ui_component: "frm-search",
                            ui_function: "input",
                            ui_action: "search",
                            ui_target: value,
                        },
                    };
                    dispatchTrackingEvent(uiEvent);
                    set((state) => ({
                        loadingMain: value,
                    }));
                    const results = yield fetchSearchResults(value);
                    set((state) => ({
                        results: results,
                        loadingMain: null,
                    }));
                }), 500);
            }
        },
        loadMore: (facetName) => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            if (get().loadingMain !== null) {
                // console.log("loadMore: A main fetch is running; why are we loading more when we are loading the main query?!");
                return;
            }
            const currentFacet = (_b = (_a = get().results) === null || _a === void 0 ? void 0 : _a.facets.find((facet) => facet.facetName === facetName)) !== null && _b !== void 0 ? _b : null;
            if (currentFacet === null) {
                // console.log("loadMore: We don't have any results for this facet; can't load more.");
                return;
            }
            const loadedItems = currentFacet.items;
            if (loadedItems.length >= currentFacet.total) {
                // console.log("loadMore: We already have all results; can't load more.");
                return;
            }
            const uiEvent = {
                name: "ui",
                payload: {
                    ui_component: "frm-search",
                    ui_function: "load",
                    ui_action: "results",
                    ui_target: `${get().query}/${facetName}/${loadedItems.length}`,
                },
            };
            dispatchTrackingEvent(uiEvent);
            set((state) => ({
                loadingMore: facetName,
            }));
            const apiResults = yield fetchMoreResults(get().query, currentFacet);
            let responseError = null;
            if (apiResults === null) {
                responseError = "loadMore: Fetch failed; don't update state.";
            }
            else if (apiResults.facets.length !== 1) {
                responseError = "loadMore: We expect only one facet; don't update state.";
            }
            else if (apiResults.facets[0].facetName !== facetName) {
                responseError = "loadMore: We expect only the facet we requested; don't update state.";
            }
            else if (apiResults.facets[0].items.length === 0) {
                // responseError = "loadMore: We expect at least one result; don't update state.";
                // Actually we DO have to update state to update the total count which can change if the load more includes an item that for some reason cannot be rendered as search result struct
            }
            if (responseError !== null) {
                set((state) => ({
                    loadingMore: null,
                }));
                return;
            }
            set((state) => {
                // console.log("loadMore: Merge into existing results");
                if (state.results === null) {
                    return {};
                }
                // console.log("pre merge", state.results, apiResults);
                const newResult = Object.assign({}, state.results);
                newResult.facets = newResult.facets.map((facet) => {
                    if (facet.facetName !== facetName) {
                        return facet;
                    }
                    const newFacet = Object.assign({}, facet);
                    newFacet.items = [...facet.items, ...apiResults.facets[0].items];
                    newFacet.total = apiResults.facets[0].total;
                    return newFacet;
                });
                // console.log("post merge", newResult);
                return {
                    results: newResult,
                    loadingMore: null,
                };
            });
        }),
    },
}));
export const useQuery = () => useSearchStore((state) => state.query);
export const useShowHeader = () => useSearchStore((state) => state.showHeader);
export const useResults = () => useSearchStore((state) => state.results);
export const useLoadingMain = () => useSearchStore((state) => state.loadingMain);
export const useLoadingMore = () => useSearchStore((state) => state.loadingMore);
export const useSearchActions = () => useSearchStore((state) => state.actions);
// API calls
const fetchSearchResults = (query) => __awaiter(void 0, void 0, void 0, function* () {
    if (query.length === 0) {
        return null;
    }
    const headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("X-SITEACCESS", location.pathname.split("/")[1]);
    fetchSearchResultsController = new AbortController();
    const options = {
        method: "GET",
        headers: headers,
        signal: fetchSearchResultsController.signal,
    };
    // Remember url escaping get values ;-)
    const url = IBEXA_SEARCH_ENDPOINT +
        "?" +
        new URLSearchParams({
            q: query,
        });
    return yield fetch(url, options)
        .then((response) => response.json())
        .then((data) => {
        return data.Search.result;
    })
        .catch((error) => {
        if (error.name === "AbortError") {
            // console.info("Fetch aborted");
        }
        else {
            console.error("Error fetching search results", error);
        }
        return null;
    })
        .finally(() => {
        fetchSearchResultsController = null;
    });
});
const fetchMoreResults = (query, facet) => __awaiter(void 0, void 0, void 0, function* () {
    const headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("X-SITEACCESS", location.pathname.split("/")[1]);
    fetchMoreResultsController = new AbortController();
    const options = {
        method: "GET",
        headers: headers,
        signal: fetchMoreResultsController.signal,
    };
    // Remember url escaping get values ;-)
    const url = IBEXA_SEARCH_ENDPOINT +
        "/" +
        facet.facetName +
        "?" +
        new URLSearchParams({
            q: query,
            start: facet.items.length.toString(),
            num: "8",
        });
    return yield fetch(url, options)
        .then((response) => response.json())
        .then((data) => {
        return data.Search.result;
    })
        .catch((error) => {
        if (error.name === "AbortError") {
            // console.info("Fetch aborted");
        }
        else {
            console.error("Error fetching search results", error);
        }
        return null;
    })
        .finally(() => {
        fetchMoreResultsController = null;
    });
});
